import { cloneState } from "./player.ts";
import type {
  DemoScene,
  ReplicaMessage,
  ReplicaState,
  SceneId,
  TimedOp,
  WorkCard
} from "./types.ts";
import {
  CHEN_PERSON_ID,
  COLLAB_V2_FILE,
  CRM_AGENT_ID,
  DIRECT_ZHOU_ID,
  DRAFT_V1_FILE,
  EXCEL_AGENT_ID,
  FINAL_FILES,
  FINAL_V3_FILE,
  KNOWLEDGE_AGENT_ID,
  LEDGER_FILE,
  MEETING_AGENT_ID,
  MEETING_NOTES_FILE,
  MEETING_REVIEW_ID,
  PPT_AGENT_ID,
  PRESENTATION_FILE,
  PROJECT_CHENXING,
  RFP_FILE,
  SOURCE_FILES,
  THREAD_MAIN,
  THREAD_MEETING,
  WORKBOOK_FILE,
  ZHOU_PERSON_ID,
  baseWorld,
  withAgents
} from "./world.ts";

const DRAFT_V1_CARD: Extract<WorkCard, { kind: "draft" }> = {
  kind: "draft",
  file: DRAFT_V1_FILE,
  stats: [
    { label: "客户引用", value: "4" },
    { label: "企业知识", value: "3" },
    { label: "待确认", value: "2" }
  ],
  changes: ["客户硬约束", "建议响应", "待团队确认", "测算待办"]
};

const WORKBOOK_CARD: Extract<WorkCard, { kind: "workbook" }> = {
  kind: "workbook",
  file: WORKBOOK_FILE,
  metrics: [
    { label: "预计节能率", value: "17.8%" },
    { label: "静态回收期", value: "31.4 个月" }
  ],
  changes: ["假设!B4 · 基线周期", "假设!B8 · 综合电价", "测算!F12:F24 · 回收期公式"]
};

const COLLAB_V2_CARD: Extract<WorkCard, { kind: "draft" }> = {
  kind: "draft",
  file: COLLAB_V2_FILE,
  stats: [
    { label: "已确认", value: "2" },
    { label: "测算结论", value: "2" },
    { label: "保留引用", value: "7" }
  ],
  changes: ["加入 17.8% / 31.4 个月测算", "改为按产线分批切换", "链接 ROI 工作簿"]
};

export const MEETING_SUMMARY_CARD: Extract<WorkCard, { kind: "meeting-summary" }> = {
  kind: "meeting-summary",
  file: MEETING_NOTES_FILE,
  stats: [
    { label: "会议历史", value: "18:06" },
    { label: "候选决定", value: "3" },
    { label: "待办", value: "3" }
  ],
  decisions: [
    "31.4 个月写为“当前输入下的测算值” · 10:03 周妍",
    "按产线分批切换，每条产线不超过 4 小时 · 10:07 陈航",
    "停机损失列为风险，不进入当前 ROI · 10:14 周妍"
  ]
};

const FINAL_V3_CARD: Extract<WorkCard, { kind: "draft" }> = {
  kind: "draft",
  file: FINAL_V3_FILE,
  stats: [
    { label: "会议决定", value: "3/3" },
    { label: "待确认", value: "0" },
    { label: "来源", value: "10" }
  ],
  changes: ["写回三项会议决定", "补充回退方案", "保留会议时间点与原始引用"]
};

const PRESENTATION_CARD: Extract<WorkCard, { kind: "presentation" }> = {
  kind: "presentation",
  file: PRESENTATION_FILE,
  sources: ["澄清会定稿-v3.md", "节能改造ROI测算-v3.xlsx", "内部预审纪要.md"],
  slides: ["客户目标与边界", "方案路径", "ROI 测算", "分线切换", "风险与下一步"]
};

function crmCard(status: "preview" | "written"): Extract<WorkCard, { kind: "crm" }> {
  return {
    kind: "crm",
    system: "企业 CRM · 晨星汽车园区节能改造",
    status,
    changes: [
      { label: "商机阶段", before: "方案准备", after: "客户澄清" },
      { label: "下一步", before: "未安排", after: "今天 15:00 客户澄清会" },
      { label: "测算口径", before: "待确认", after: "17.8% · 31.4 个月" }
    ],
    attachments: [WORKBOOK_FILE.name, PRESENTATION_FILE.name]
  };
}

function directInitial(
  sceneId: SceneId,
  target: "human" | "knowledge",
  messages: readonly ReplicaMessage[] = []
): ReplicaState {
  const base = cloneState(baseWorld(sceneId));
  const human = target === "human";
  return withAgents(
    {
      ...base,
      view: "direct",
      space: {
        ...base.space,
        name: human ? "周妍" : "知识库专家",
        badge: human ? "同事" : "智能体",
        members: human ? "Direct Chat" : "Agent Direct Chat",
        tab: "messages",
        threadTitle: human ? "周妍" : "知识库专家",
        threadMeta: human ? "客户经理 · 在线" : "@knowledge-expert",
        composerPlaceholder: human ? "发送给周妍" : "发送给知识库专家"
      },
      messages,
      hud: {
        ...base.hud,
        summary: human ? "当前对话" : "1 个智能体在场",
        fileActivities: human ? [] : base.hud.fileActivities,
        recentThreads: []
      },
      active: {
        threadId: null,
        projectId: null,
        agentId: human ? null : KNOWLEDGE_AGENT_ID,
        personId: human ? ZHOU_PERSON_ID : null,
        directChatId: human ? DIRECT_ZHOU_ID : null,
        meetingId: null,
        files: false
      }
    },
    {
      [KNOWLEDGE_AGENT_ID]: {
        status: human ? "present" : "idle",
        statusLabel: human ? "在场" : "待命"
      }
    }
  );
}

function intakeInitial(): ReplicaState {
  return directInitial("intake-dm", "human");
}

function intakeTimeline(): TimedOp[] {
  const request =
    "晨星汽车把园区节能改造 RFP 发回来了。今天 15:00 开澄清会，客户想先确认三年回收期能不能成立。中午前能给一版可讲的材料吗？";
  const reply = "收到。我先核硬约束和测算口径，12:00 前把评审版放回这里。";
  return [
    {
      at: 320,
      op: {
        op: "pushMessage",
        message: {
          id: "zhou-intake",
          kind: "human",
          personId: ZHOU_PERSON_ID,
          time: "09:12",
          appearAt: 320,
          text: request,
          attachments: [RFP_FILE, LEDGER_FILE]
        }
      }
    },
    { at: 1900, op: { op: "typeComposer", text: reply, duration: 950 } },
    { at: 2920, op: { op: "setComposer", text: "" } },
    {
      at: 2920,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-intake-reply",
          kind: "user",
          time: "09:13",
          appearAt: 2920,
          text: reply
        }
      }
    },
    {
      at: 3200,
      op: {
        op: "setDirectChat",
        id: DIRECT_ZHOU_ID,
        patch: { preview: reply, time: "09:13", unread: 0 }
      }
    }
  ];
}

function knowledgeInitial(): ReplicaState {
  return directInitial("knowledge-draft", "knowledge");
}

function knowledgeTimeline(): TimedOp[] {
  return [
    {
      at: 260,
      op: {
        op: "typeComposer",
        text: "结合这份 RFP 和企业知识库，起草澄清会材料。每条保留来源。",
        duration: 850
      }
    },
    { at: 1160, op: { op: "setComposer", text: "" } },
    {
      at: 1160,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-kb-request",
          kind: "user",
          time: "09:14",
          appearAt: 1160,
          mentionAgentId: KNOWLEDGE_AGENT_ID,
          mentionName: "知识库专家",
          text: "结合这份 RFP 和企业知识库，起草澄清会材料。列出必须响应项、建议口径和不能直接承诺的内容，每条保留来源。",
          attachments: [RFP_FILE]
        }
      }
    },
    {
      at: 1380,
      op: { op: "setAgent", id: KNOWLEDGE_AGENT_ID, status: "working", statusLabel: "正在检索" }
    },
    {
      at: 1500,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-search",
          kind: "thinking",
          time: "09:14",
          appearAt: 1500,
          agentId: KNOWLEDGE_AGENT_ID,
          label: "正在同时读取会话文件与企业知识",
          elapsed: "0:02",
          open: true,
          steps: [
            {
              id: "k1",
              text: "读取 RFP：节能目标、停机窗口、投资回收",
              done: false,
              source: "file"
            }
          ]
        }
      }
    },
    {
      at: 2600,
      op: {
        op: "replaceMessage",
        id: "knowledge-search",
        message: {
          id: "knowledge-search",
          kind: "thinking",
          time: "09:14",
          appearAt: 1500,
          agentId: KNOWLEDGE_AGENT_ID,
          label: "正在检索企业 Knowledge Base",
          elapsed: "0:05",
          open: true,
          steps: [
            { id: "k1", text: "读取 RFP：节能目标、停机窗口、投资回收", done: true, source: "file" },
            { id: "k2", text: "汽车制造园区节能案例库 · 3 个同类案例", done: true, source: "knowledge" },
            { id: "k3", text: "售前承诺边界手册（2026）", done: false, source: "knowledge" }
          ]
        }
      }
    },
    {
      at: 3500,
      op: {
        op: "replaceMessage",
        id: "knowledge-search",
        message: {
          id: "knowledge-search",
          kind: "thinking",
          time: "09:14",
          appearAt: 1500,
          agentId: KNOWLEDGE_AGENT_ID,
          label: "已整理客户要求与企业经验",
          elapsed: "0:08",
          open: true,
          steps: [
            { id: "k1", text: "RFP 客户硬约束 · 4 条引用", done: true, source: "file" },
            { id: "k2", text: "汽车制造园区节能案例库 · 3 个同类案例", done: true, source: "knowledge" },
            { id: "k3", text: "售前承诺边界手册（2026）", done: true, source: "knowledge" },
            { id: "k4", text: "工业节能项目测算口径 v4", done: true, source: "knowledge" }
          ]
        }
      }
    },
    { at: 4000, op: { op: "removeMessage", id: "knowledge-search" } },
    {
      at: 4100,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-preview",
          kind: "answer",
          time: "09:15",
          appearAt: 4100,
          agentId: KNOWLEDGE_AGENT_ID,
          paragraphs: [
            "RFP 要求节能率不低于 15%、每条产线切换不超过 4 小时，并把 36 个月写成目标。企业同类案例通常为 16%–20%，但不能直接套用。",
            "当前缺少客户确认的基线周期和综合电价。历史案例是否只用于说明方案路径？"
          ]
        }
      }
    },
    {
      at: 5200,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-corrects",
          kind: "user",
          time: "09:16",
          appearAt: 5200,
          text: "对。不要把案例区间写成客户预期；回收期留待测算。再补“不停产切换”，但标成需要交付工程师确认。"
        }
      }
    },
    {
      at: 6000,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-confirms",
          kind: "answer",
          time: "09:17",
          appearAt: 6000,
          agentId: KNOWLEDGE_AGENT_ID,
          paragraphs: [
            "已按“客户硬约束、建议响应、待团队确认、测算待办”重组。36 个月统一写为“目标”，不使用“保证”，可以吗？"
          ]
        }
      }
    },
    {
      at: 6700,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-confirms",
          kind: "user",
          time: "09:17",
          appearAt: 6700,
          text: "可以。所有未经客户或测算确认的数字都标成待确认，生成初稿。"
        }
      }
    },
    {
      at: 7080,
      op: { op: "setAgent", id: KNOWLEDGE_AGENT_ID, status: "working", statusLabel: "正在生成初稿" }
    },
    {
      at: 7220,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-drafting",
          kind: "thinking",
          time: "09:17",
          appearAt: 7220,
          agentId: KNOWLEDGE_AGENT_ID,
          label: "正在写入有来源的协作初稿",
          elapsed: "0:03",
          open: true,
          steps: [
            { id: "d1", text: "保留 4 条客户 RFP 引用", done: true, source: "file" },
            { id: "d2", text: "链接 3 条企业知识依据", done: true, source: "knowledge" },
            { id: "d3", text: "标记 2 个待团队确认项", done: false, source: "system" }
          ]
        }
      }
    },
    { at: 7900, op: { op: "removeMessage", id: "knowledge-drafting" } },
    {
      at: 7980,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-draft-result",
          kind: "answer",
          time: "09:18",
          appearAt: 7980,
          agentId: KNOWLEDGE_AGENT_ID,
          paragraphs: ["初稿已生成。它保留了客户文件与企业知识的两类来源，团队可以从这份文件继续。"],
          card: DRAFT_V1_CARD
        }
      }
    },
    {
      at: 8400,
      op: { op: "setAgent", id: KNOWLEDGE_AGENT_ID, status: "present", statusLabel: "初稿已完成" }
    }
  ];
}

function teamInitial(): ReplicaState {
  const base = cloneState(baseWorld("team-thread"));
  return withAgents(
    {
      ...base,
      files: [...SOURCE_FILES, DRAFT_V1_FILE],
      active: {
        ...base.active,
        threadId: THREAD_MAIN,
        projectId: PROJECT_CHENXING,
        agentId: null
      }
    },
    {
      [KNOWLEDGE_AGENT_ID]: { status: "present", statusLabel: "在场" },
      [EXCEL_AGENT_ID]: { status: "idle", statusLabel: "等待输入" }
    }
  );
}

function teamTimeline(): TimedOp[] {
  return [
    {
      at: 220,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-shares-draft",
          kind: "user",
          time: "09:20",
          appearAt: 220,
          mentionAgentId: EXCEL_AGENT_ID,
          mentionName: "Excel 专家",
          text: "这是我和知识库专家整理的 v1。周妍确认客户口径，陈航确认切换约束；输入齐了以后请从这份初稿和台账继续测算。",
          attachments: [DRAFT_V1_FILE, RFP_FILE, LEDGER_FILE]
        }
      }
    },
    {
      at: 1050,
      op: {
        op: "pushMessage",
        message: {
          id: "zhou-baseline",
          kind: "human",
          personId: ZHOU_PERSON_ID,
          time: "09:21",
          appearAt: 1050,
          text: "客户确认：以 2025 全年为基线，综合电价按 0.82 元/kWh。36 个月只能写目标。"
        }
      }
    },
    {
      at: 1750,
      op: {
        op: "pushMessage",
        message: {
          id: "chen-window",
          kind: "human",
          personId: CHEN_PERSON_ID,
          time: "09:21",
          appearAt: 1750,
          text: "4 小时是单条产线的切换窗口，可以不停全厂。初稿第二部分改成分线切换。"
        }
      }
    },
    {
      at: 2250,
      op: { op: "setAgent", id: EXCEL_AGENT_ID, status: "working", statusLabel: "正在读表" }
    },
    {
      at: 2350,
      op: {
        op: "pushMessage",
        message: {
          id: "excel-reading",
          kind: "thinking",
          time: "09:22",
          appearAt: 2350,
          agentId: EXCEL_AGENT_ID,
          label: "正在读取初稿、台账与团队确认",
          elapsed: "0:05",
          open: true,
          steps: [
            { id: "e1", text: "读取 12 个工作表与公式依赖", done: true, source: "file" },
            { id: "e2", text: "写入基线周期与综合电价参数", done: true, source: "system" },
            { id: "e3", text: "检查投资口径与停机损失", done: false, source: "system" }
          ]
        }
      }
    },
    { at: 3200, op: { op: "removeMessage", id: "excel-reading" } },
    {
      at: 3280,
      op: {
        op: "pushMessage",
        message: {
          id: "excel-question",
          kind: "answer",
          time: "09:23",
          appearAt: 3280,
          agentId: EXCEL_AGENT_ID,
          paragraphs: ["台账中的设备投资包含税费，但不包含停机损失。回收期是否按同一口径计算？"]
        }
      }
    },
    {
      at: 4100,
      op: {
        op: "pushMessage",
        message: {
          id: "zhou-investment",
          kind: "human",
          personId: ZHOU_PERSON_ID,
          time: "09:23",
          appearAt: 4100,
          text: "不包含停机损失，在风险项单列。"
        }
      }
    },
    {
      at: 4550,
      op: {
        op: "pushMessage",
        message: {
          id: "chen-investment",
          kind: "human",
          personId: CHEN_PERSON_ID,
          time: "09:23",
          appearAt: 4550,
          text: "确认，停机损失暂不量化。"
        }
      }
    },
    {
      at: 4900,
      op: {
        op: "pushMessage",
        message: {
          id: "excel-calculating",
          kind: "thinking",
          time: "09:24",
          appearAt: 4900,
          agentId: EXCEL_AGENT_ID,
          label: "正在核对公式并生成修订稿",
          elapsed: "0:11",
          open: true,
          steps: [
            { id: "ec1", text: "重算 2025 基线与节能率", done: true, source: "system" },
            { id: "ec2", text: "验证回收期公式 F12:F24", done: true, source: "system" },
            { id: "ec3", text: "保留原表并新增变更说明", done: false, source: "file" }
          ]
        }
      }
    },
    { at: 5750, op: { op: "removeMessage", id: "excel-calculating" } },
    {
      at: 5840,
      op: {
        op: "pushMessage",
        message: {
          id: "excel-result",
          kind: "answer",
          time: "09:24",
          appearAt: 5840,
          agentId: EXCEL_AGENT_ID,
          paragraphs: ["测算完成。原始工作表未覆盖，所有变化都记录在“变更说明”中。"],
          card: WORKBOOK_CARD
        }
      }
    },
    {
      at: 6100,
      op: { op: "setAgent", id: EXCEL_AGENT_ID, status: "present", statusLabel: "测算已完成" }
    },
    {
      at: 6500,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-update-draft",
          kind: "user",
          time: "09:25",
          appearAt: 6500,
          mentionAgentId: KNOWLEDGE_AGENT_ID,
          mentionName: "知识库专家",
          text: "把团队确认和 Excel 结果写回 v1。31.4 个月写成“当前输入下的测算值”，并链接测算表。"
        }
      }
    },
    {
      at: 6900,
      op: { op: "setAgent", id: KNOWLEDGE_AGENT_ID, status: "working", statusLabel: "正在修订" }
    },
    {
      at: 7300,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-v2",
          kind: "answer",
          time: "09:26",
          appearAt: 7300,
          agentId: KNOWLEDGE_AGENT_ID,
          paragraphs: ["已把两位同事确认的口径和 Excel 结果写回初稿，保留原始引用与工作簿链接。"],
          card: COLLAB_V2_CARD
        }
      }
    },
    {
      at: 7520,
      op: { op: "setAgent", id: KNOWLEDGE_AGENT_ID, status: "present", statusLabel: "协作稿已更新" }
    },
    {
      at: 7900,
      op: {
        op: "pushMessage",
        message: {
          id: "zhou-meeting",
          kind: "human",
          personId: ZHOU_PERSON_ID,
          time: "09:27",
          appearAt: 7900,
          text: "数据口径可以进入内部预审。10:00 我们线上过一遍。"
        }
      }
    },
    {
      at: 8250,
      op: {
        op: "pushMessage",
        message: {
          id: "meeting-scheduled",
          kind: "meeting-event",
          time: "09:27",
          appearAt: 8250,
          title: "晨星汽车内部方案预审",
          detail: "10:00 · 林深、周妍、陈航 · 会议纪要专家已授权",
          status: "scheduled",
          sceneId: "meeting-review"
        }
      }
    }
  ];
}

function meetingInitial(): ReplicaState {
  const base = cloneState(baseWorld("meeting-review"));
  if (!base.meeting) return base;
  return withAgents(
    {
      ...base,
      view: "meeting",
      files: [...SOURCE_FILES, DRAFT_V1_FILE, WORKBOOK_FILE, COLLAB_V2_FILE],
      meeting: {
        ...base.meeting,
        status: "live",
        statusLabel: "记录中",
        duration: "00:18"
      },
      active: {
        ...base.active,
        threadId: THREAD_MEETING,
        meetingId: MEETING_REVIEW_ID
      }
    },
    {
      [KNOWLEDGE_AGENT_ID]: { status: "present", statusLabel: "在场" },
      [EXCEL_AGENT_ID]: { status: "present", statusLabel: "测算已完成" },
      [MEETING_AGENT_ID]: { status: "working", statusLabel: "记录中" }
    }
  );
}

function meetingTimeline(): TimedOp[] {
  return [
    {
      at: 500,
      op: {
        op: "pushMeetingLine",
        line: {
          id: "m1",
          personId: ZHOU_PERSON_ID,
          timestamp: "10:03",
          text: "31.4 个月可以讲，但必须写“当前输入下的测算值”，36 个月仍是客户目标。"
        }
      }
    },
    {
      at: 650,
      op: {
        op: "setMeeting",
        patch: { duration: "03:24", syncedSegments: 8, candidateDecisions: 1 }
      }
    },
    {
      at: 1900,
      op: {
        op: "pushMeetingLine",
        line: {
          id: "m2",
          personId: CHEN_PERSON_ID,
          timestamp: "10:07",
          text: "4 小时是每条产线的切换窗口，不是全厂停机。客户版还要补回退方案。"
        }
      }
    },
    {
      at: 2050,
      op: {
        op: "setMeeting",
        patch: { duration: "07:48", syncedSegments: 19, candidateDecisions: 2, actions: 1 }
      }
    },
    {
      at: 3300,
      op: {
        op: "pushMeetingLine",
        line: {
          id: "m3",
          personId: "self",
          timestamp: "10:11",
          text: "历史案例只证明方案路径，不把案例数字放进晨星的结论。"
        }
      }
    },
    {
      at: 3450,
      op: {
        op: "setMeeting",
        patch: { duration: "11:32", syncedSegments: 29, candidateDecisions: 2, actions: 2 }
      }
    },
    {
      at: 4650,
      op: {
        op: "pushMeetingLine",
        line: {
          id: "m4",
          personId: ZHOU_PERSON_ID,
          timestamp: "10:14",
          text: "停机损失不进回收期，放风险项，客户澄清后再量化。"
        }
      }
    },
    {
      at: 4800,
      op: {
        op: "setMeeting",
        patch: {
          duration: "14:56",
          syncedSegments: 37,
          candidateDecisions: 3,
          resolvedConflicts: 1,
          actions: 3
        }
      }
    },
    {
      at: 5550,
      op: {
        op: "setMeeting",
        patch: {
          status: "ended",
          statusLabel: "会议已结束",
          duration: "18:06",
          syncedSegments: 42
        }
      }
    },
    {
      at: 5800,
      op: {
        op: "setMeeting",
        patch: { summary: MEETING_SUMMARY_CARD }
      }
    },
    {
      at: 6900,
      op: {
        op: "setMeeting",
        patch: { confirmed: true }
      }
    },
    {
      at: 7000,
      op: { op: "setAgent", id: MEETING_AGENT_ID, status: "present", statusLabel: "纪要已确认" }
    }
  ];
}

function finalizeInitial(): ReplicaState {
  const base = cloneState(baseWorld("finalize-thread"));
  const meetingSummary: ReplicaMessage = {
    id: "meeting-summary-thread",
    kind: "answer",
    time: "10:18",
    appearAt: -1,
    agentId: MEETING_AGENT_ID,
    paragraphs: ["林深已确认 3 项会议决定，原始时间点随纪要保留。"],
    card: MEETING_SUMMARY_CARD
  };
  return withAgents(
    {
      ...base,
      messages: [meetingSummary],
      files: FINAL_FILES.map((file) => ({ ...file })),
      hud: {
        ...base.hud,
        summary: "3 个智能体在场",
        fileActivities: [
          {
            id: "meeting-notes",
            name: MEETING_NOTES_FILE.name,
            detail: "会议纪要专家 · 已确认",
            time: "10:18",
            kind: "markdown",
            operation: "created"
          },
          {
            id: "workbook-v3",
            name: WORKBOOK_FILE.name,
            detail: "Excel 专家 · 新版本",
            time: "09:24",
            kind: "xlsx",
            operation: "updated"
          }
        ]
      }
    },
    {
      [KNOWLEDGE_AGENT_ID]: { status: "idle", statusLabel: "等待修订" },
      [EXCEL_AGENT_ID]: { status: "present", statusLabel: "测算已完成" },
      [MEETING_AGENT_ID]: { status: "present", statusLabel: "纪要已确认" },
      [PPT_AGENT_ID]: { status: "idle", statusLabel: "待命" },
      [CRM_AGENT_ID]: { status: "idle", statusLabel: "待命" }
    }
  );
}

export function crmMessage(status: "preview" | "written", appearAt: number): ReplicaMessage {
  return {
    id: "crm-result",
    kind: "answer",
    time: status === "written" ? "10:26" : "10:25",
    appearAt,
    agentId: CRM_AGENT_ID,
    paragraphs: [
      status === "written"
        ? "已按批准内容写回关联商机，字段校验与附件关联均成功。"
        : "我已从当前 Project、Thread 和会议纪要整理出 CRM 变更。确认前不会写入。"
    ],
    card: crmCard(status)
  };
}

function finalizeTimeline(): TimedOp[] {
  return [
    {
      at: 350,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-finalize-copy",
          kind: "user",
          time: "10:19",
          appearAt: 350,
          mentionAgentId: KNOWLEDGE_AGENT_ID,
          mentionName: "知识库专家",
          text: "按已确认的会议决定更新 v2，保留每项决定对应的会议时间点和原引用。"
        }
      }
    },
    {
      at: 650,
      op: { op: "setAgent", id: KNOWLEDGE_AGENT_ID, status: "working", statusLabel: "正在写回决定" }
    },
    {
      at: 1320,
      op: {
        op: "pushMessage",
        message: {
          id: "knowledge-v3",
          kind: "answer",
          time: "10:20",
          appearAt: 1320,
          agentId: KNOWLEDGE_AGENT_ID,
          paragraphs: ["三项会议决定已写回协作稿，会议时间点、RFP 与企业知识引用均已保留。"],
          card: FINAL_V3_CARD
        }
      }
    },
    {
      at: 2100,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-ppt-request",
          kind: "user",
          time: "10:22",
          appearAt: 2100,
          mentionAgentId: PPT_AGENT_ID,
          mentionName: "PPT 专家",
          text: "使用售前评审模板，把 v3 定稿和 ROI 工作簿做成 8 页。所有数字只从已确认来源引用。"
        }
      }
    },
    {
      at: 2400,
      op: { op: "setAgent", id: PPT_AGENT_ID, status: "working", statusLabel: "正在排版" }
    },
    {
      at: 2600,
      op: {
        op: "pushMessage",
        message: {
          id: "ppt-building",
          kind: "thinking",
          time: "10:22",
          appearAt: 2600,
          agentId: PPT_AGENT_ID,
          label: "正在使用企业模板生成客户演示",
          elapsed: "0:14",
          open: true,
          steps: [
            { id: "p1", text: "从 v3 提取已确认章节", done: true, source: "file" },
            { id: "p2", text: "从 XLSX 生成 ROI 图表", done: true, source: "file" },
            { id: "p3", text: "检查 8 页内容与来源", done: false, source: "system" }
          ]
        }
      }
    },
    { at: 3900, op: { op: "removeMessage", id: "ppt-building" } },
    {
      at: 3980,
      op: {
        op: "pushMessage",
        message: {
          id: "ppt-result",
          kind: "answer",
          time: "10:24",
          appearAt: 3980,
          agentId: PPT_AGENT_ID,
          paragraphs: ["8 页评审材料已完成，数字与风险项都能回到定稿、工作簿或会议纪要。"],
          card: PRESENTATION_CARD
        }
      }
    },
    {
      at: 4850,
      op: { op: "setAgent", id: CRM_AGENT_ID, status: "working", statusLabel: "正在读取商机" }
    },
    {
      at: 5050,
      op: {
        op: "pushMessage",
        message: {
          id: "crm-reading",
          kind: "thinking",
          time: "10:25",
          appearAt: 5050,
          agentId: CRM_AGENT_ID,
          label: "正在生成字段级 CRM 变更预览",
          elapsed: "0:04",
          open: true,
          steps: [
            { id: "c1", text: "读取关联商机与当前阶段", done: true, source: "system" },
            { id: "c2", text: "整理会议决定与下一步", done: true, source: "system" },
            { id: "c3", text: "准备附件关联 · 尚未写入", done: false, source: "file" }
          ]
        }
      }
    },
    { at: 5900, op: { op: "removeMessage", id: "crm-reading" } },
    { at: 5980, op: { op: "pushMessage", message: crmMessage("preview", 5980) } },
    {
      at: 7350,
      op: { op: "replaceMessage", id: "crm-result", message: crmMessage("written", 5980) }
    },
    {
      at: 7420,
      op: { op: "setAgent", id: CRM_AGENT_ID, status: "present", statusLabel: "已写入 CRM" }
    }
  ];
}

function deliveryInitial(): ReplicaState {
  const seeded: ReplicaMessage[] = [
    {
      id: "zhou-intake-history",
      kind: "human",
      personId: ZHOU_PERSON_ID,
      time: "09:12",
      appearAt: -1,
      text: "晨星汽车今天 15:00 开澄清会，中午前能给一版可讲的材料吗？",
      attachments: [RFP_FILE, LEDGER_FILE]
    },
    {
      id: "lin-intake-history",
      kind: "user",
      time: "09:13",
      appearAt: -1,
      text: "收到。我先核硬约束和测算口径，12:00 前把评审版放回这里。"
    }
  ];
  const state = directInitial("delivery-dm", "human", seeded);
  return {
    ...state,
    files: FINAL_FILES.map((file) => ({ ...file })),
    directChats: state.directChats.map((chat) =>
      chat.id === DIRECT_ZHOU_ID
        ? { ...chat, preview: "预审意见已全部合入，文件发你了。", time: "10:28", unread: 0 }
        : chat
    )
  };
}

function deliveryTimeline(): TimedOp[] {
  const message =
    "预审意见已全部合入。当前输入下回收期是 31.4 个月，P7 单列了基线、电价和停机窗口；CRM 的商机阶段和下午会议也已更新。";
  return [
    { at: 350, op: { op: "typeComposer", text: message, duration: 850 } },
    { at: 1250, op: { op: "setComposer", text: "" } },
    {
      at: 1250,
      op: {
        op: "pushMessage",
        message: {
          id: "lin-delivery",
          kind: "user",
          time: "10:28",
          appearAt: 1250,
          text: message,
          attachments: [WORKBOOK_FILE, PRESENTATION_FILE]
        }
      }
    },
    {
      at: 2300,
      op: {
        op: "pushMessage",
        message: {
          id: "zhou-delivery-reply",
          kind: "human",
          personId: ZHOU_PERSON_ID,
          time: "10:29",
          appearAt: 2300,
          text: "收到，我会前再过一遍。客户新增问题继续放项目里。"
        }
      }
    },
    {
      at: 2450,
      op: {
        op: "setDirectChat",
        id: DIRECT_ZHOU_ID,
        patch: { preview: "收到，我会前再过一遍。", time: "10:29", unread: 1 }
      }
    }
  ];
}

function filesInitial(): ReplicaState {
  const base = cloneState(baseWorld("project-files"));
  return withAgents(
    {
      ...base,
      view: "files",
      space: { ...base.space, tab: "documents" },
      files: FINAL_FILES.map((file) => ({ ...file })),
      active: {
        ...base.active,
        threadId: null,
        agentId: null,
        meetingId: null,
        files: true
      },
      hud: {
        ...base.hud,
        fileActivities: [
          {
            id: "presentation-final",
            name: PRESENTATION_FILE.name,
            detail: "PPT 专家 · 评审版",
            time: "10:24",
            kind: "pptx",
            operation: "created"
          },
          {
            id: "meeting-notes-final",
            name: MEETING_NOTES_FILE.name,
            detail: "会议纪要专家 · 已确认",
            time: "10:18",
            kind: "markdown",
            operation: "created"
          },
          {
            id: "workbook-final",
            name: WORKBOOK_FILE.name,
            detail: "Excel 专家 · 可继续编辑",
            time: "09:24",
            kind: "xlsx",
            operation: "updated"
          }
        ]
      }
    },
    {
      [KNOWLEDGE_AGENT_ID]: { status: "present", statusLabel: "定稿已完成" },
      [EXCEL_AGENT_ID]: { status: "present", statusLabel: "测算已完成" },
      [MEETING_AGENT_ID]: { status: "present", statusLabel: "纪要已确认" },
      [PPT_AGENT_ID]: { status: "present", statusLabel: "评审版已完成" },
      [CRM_AGENT_ID]: { status: "present", statusLabel: "已写入 CRM" }
    }
  );
}

function scene(id: SceneId, duration: number, hold: number, initial: ReplicaState, timeline: TimedOp[]): DemoScene {
  return { id, duration, hold, initial, timeline };
}

function pace(timeline: TimedOp[], factor: number): TimedOp[] {
  return timeline.map((item) => {
    if (item.op.op === "typeComposer") {
      return {
        at: Math.round(item.at * factor),
        op: { ...item.op, duration: Math.round(item.op.duration * Math.max(1.25, factor * 0.9)) }
      };
    }
    return { at: Math.round(item.at * factor), op: item.op };
  });
}

function runLength(timeline: TimedOp[], tail: number): number {
  return (
    timeline.reduce((max, item) => {
      const end = item.op.op === "typeComposer" ? item.at + item.op.duration : item.at;
      return Math.max(max, end);
    }, 0) + tail
  );
}

const intakeOps = pace(intakeTimeline(), 1.45);
const knowledgeOps = pace(knowledgeTimeline(), 1.75);
const teamOps = pace(teamTimeline(), 1.65);
const meetingOps = pace(meetingTimeline(), 1.55);
const finalizeOps = pace(finalizeTimeline(), 1.65);
const deliveryOps = pace(deliveryTimeline(), 1.4);

export const DEMO_SCENES: Record<SceneId, DemoScene> = {
  "intake-dm": scene("intake-dm", runLength(intakeOps, 700), 1800, intakeInitial(), intakeOps),
  "knowledge-draft": scene("knowledge-draft", runLength(knowledgeOps, 900), 2600, knowledgeInitial(), knowledgeOps),
  "team-thread": scene("team-thread", runLength(teamOps, 800), 2400, teamInitial(), teamOps),
  "meeting-review": scene("meeting-review", runLength(meetingOps, 900), 2600, meetingInitial(), meetingOps),
  "finalize-thread": scene("finalize-thread", runLength(finalizeOps, 800), 2600, finalizeInitial(), finalizeOps),
  "delivery-dm": scene("delivery-dm", runLength(deliveryOps, 700), 3200, deliveryInitial(), deliveryOps),
  "project-files": scene("project-files", 200, 10000, filesInitial(), [])
};

export const STORY_SCENE_IDS = [
  "intake-dm",
  "knowledge-draft",
  "team-thread",
  "meeting-review",
  "finalize-thread",
  "delivery-dm"
] as const satisfies readonly SceneId[];

export const DEFAULT_SCENE_ID: SceneId = STORY_SCENE_IDS[0];
